import React, { useState, useEffect } from "react";
import "./CartModal.css";
import { useCart } from "../context/CartContext";

function CartModal({ isOpen, onClose }) {
  const [isCheckoutStage, setIsCheckoutStage] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isEatInStage1, setIsEatInStage1] = useState(false);
  const [isEatInStage2, setIsEatInStage2] = useState(false);
  const [isCurrentlyInRestaurant, setIsCurrentlyInRestaurant] = useState(null);
  const [isPickupFormStage, setIsPickupFormStage] = useState(false);
  const [tableNumber, setTableNumber] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [isFinalStage, setIsFinalStage] = useState(false);
  const [notes, setNotes] = useState("");
  const [finalOrderType, setFinalOrderType] = useState(null);
  const [finalStageData, setFinalStageData] = useState(null);

  useEffect(() => {
    if (!isOpen) {
      setIsCheckoutStage(false);
      setIsEatInStage1(false);
      setIsEatInStage2(false);
      setIsFinalStage(false);
      setIsPickupFormStage(false);
      setTableNumber("");
      setCustomerName("");
      setNotes("");
    }
  }, [isOpen]);

  const { cartItems, removeItemFromCart, updateCartItemQuantity, clearCart } =
    useCart();

  if (!isOpen) return null;
  const isCartEmpty =
    cartItems.length === 0 ||
    cartItems.reduce((total, item) => total + item.quantity, 0) === 0;

  const handleQuantityChange = (cartItemKey, change) => {
    const cartItem = cartItems.find((item) => item.key === cartItemKey);
    if (!cartItem) return;

    const newQuantity = cartItem.quantity + change;

    if (newQuantity > 0) {
      updateCartItemQuantity(cartItemKey, newQuantity);
    }
  };

  const handleCheckout = (orderType) => {
    if (orderType === "eat-in") {
      setIsEatInStage1(true);
    } else {
      setIsPickupFormStage(true);
    }
  };

  const getItemTotal = (cartItem) => {
    let total = parseFloat(cartItem.item.price || 0);

    // Required option
    if (cartItem.selectedRequiredOption) {
      total += parseFloat(cartItem.selectedRequiredOption.option_price || 0);
    }

    // Swallow options
    if (cartItem.item.swallowOptions) {
      cartItem.item.swallowOptions.forEach((opt) => {
        total += parseFloat(opt.option_price || 0) * (opt.quantity || 1);
      });
    }

    // Main proteins
    if (cartItem.item.mainProteins) {
      cartItem.item.mainProteins.forEach((opt) => {
        total += parseFloat(opt.option_price || 0) * (opt.quantity || 1);
      });
    }

    // Extra proteins
    if (cartItem.item.extraProteins) {
      cartItem.item.extraProteins.forEach((opt) => {
        total += parseFloat(opt.option_price || 0) * (opt.quantity || 1);
      });
    }

    return total * cartItem.quantity;
  };

  const cartTotal = cartItems.reduce((sum, item) => {
    return sum + getItemTotal(item);
  }, 0);

  const placeOrder = async () => {
    setIsLoading(true);

    const { customerName, tableNumber, notes, orderType } = finalStageData;

    let grandTotal = 0;

    const formattedItems = cartItems
      .map((cartItem, idx) => {
        const basePrice = parseFloat(cartItem.item?.price || 0);
        const quantity = cartItem.quantity || 1;

        const itemName =
          cartItem.item?.name ||
          cartItem.item?.item_name ||
          cartItem.name ||
          "Unnamed Item";

        let itemTotal = basePrice;

        const required = cartItem.selectedRequiredOption
          ? `${cartItem.selectedRequiredOption.option_name} (£${parseFloat(
              cartItem.selectedRequiredOption.option_price
            ).toFixed(2)})`
          : "";

        if (cartItem.selectedRequiredOption) {
          itemTotal += parseFloat(cartItem.selectedRequiredOption.option_price);
        }

        const optional =
          cartItem.selectedOptionalOptions
            ?.map((opt) => {
              const total = parseFloat(opt.option_price) * (opt.quantity || 1);
              itemTotal += total;
              return `   - ${opt.option_name} x${
                opt.quantity
              } (£${total.toFixed(2)})`;
            })
            .join("\n") || "";

        const swallow =
          cartItem.item.swallowOptions
            ?.map((opt) => {
              const total = parseFloat(opt.option_price) * (opt.quantity || 1);
              itemTotal += total;
              return `   - ${opt.option_name} x${
                opt.quantity
              } (£${total.toFixed(2)})`;
            })
            .join("\n") || "";

        const main =
          cartItem.item.mainProteins
            ?.map((opt) => {
              const total = parseFloat(opt.option_price) * (opt.quantity || 1);
              itemTotal += total;
              return `   - ${opt.option_name} x${
                opt.quantity
              } (£${total.toFixed(2)})`;
            })
            .join("\n") || "";

        const extraList = cartItem.item.extraProteins || [];
        const extra =
          extraList.length > 0
            ? `\n\n*Extra:*\n` +
              extraList
                .map((opt) => {
                  const total =
                    parseFloat(opt.option_price) * (opt.quantity || 1);
                  itemTotal += total;
                  return `   - ${opt.option_name} x${
                    opt.quantity
                  } (£${total.toFixed(2)})`;
                })
                .join("\n")
            : "";

        const totalForItem = itemTotal * quantity;
        grandTotal += totalForItem;

        return (
          `${idx + 1}. *${itemName}* (£${basePrice.toFixed(2)})` +
          `${required ? `\n   - ${required}` : ""}` +
          `${optional ? `\n${optional}` : ""}` +
          `${swallow ? `\n${swallow}` : ""}` +
          `${main ? `\n${main}` : ""}` +
          `${extra}` +
          `\nQuantity: ${quantity}` +
          `\nItem Total: £${totalForItem.toFixed(2)}`
        );
      })
      .join("\n\n");

    const message =
      `*New ${orderType} Order*\n\n` +
      `Name: ${customerName || "N/A"}\n` +
      (orderType === "Eat In" ? `Table: ${tableNumber}\n` : "") +
      (notes ? `Notes: ${notes}\n\n` : "") +
      `*Items:*\n${formattedItems}\n\n` +
      `*Order Total: £${grandTotal.toFixed(2)}*`;

    const whatsappNumber = "447368325136";

    const whatsappURL = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(
      message
    )}`;

    const isMobile = /iPhone|Android|iPad|iPod|Windows Phone/i.test(
      navigator.userAgent
    );
    const isDesktopWithWhatsAppWeb =
      /Chrome|Firefox|Edge|Safari/i.test(navigator.userAgent) &&
      window.innerWidth > 768;

    if (isMobile || isDesktopWithWhatsAppWeb) {
      window.open(whatsappURL, "_blank");
      clearCart();
      onClose();
    } else {
      alert(
        "This device does not have WhatsApp installed. Please use a device with WhatsApp or place your order in-store."
      );
    }

    setIsLoading(false);
  };

  // Pickup order function
  const confirmCheckout = (e) => {
    e.preventDefault();

    // Save pickup info
    setFinalStageData({
      customerName: customerName.trim(),
      notes: notes.trim(),
      orderType: "Pick Up",
    });

    setFinalOrderType("Pick Up");
    setIsPickupFormStage(false);
    setIsFinalStage(true);
  };

  // Eat-in order function
  const confirmEatinCheckout = (e) => {
    e.preventDefault();

    setFinalStageData({
      customerName: customerName.trim(),
      tableNumber: tableNumber.trim(),
      notes: notes.trim(),
      orderType: "Eat In",
    });

    setFinalOrderType("Eat In");
    setIsEatInStage2(false);
    setIsFinalStage(true);
  };

  const SpinnerOverlay = () => (
    <div className="spinner-overlay">
      <div className="spinner"></div>
    </div>
  );

  return (
    <div className="cart-modal-overlay">
      <div className="cart-modal-content">
        {isLoading && <SpinnerOverlay />}
        <div className="cart-modal-top">
          <h2>Your Order</h2>
          <button className="close-modal-button" onClick={onClose}>
            <i className="fas fa-times"></i>
          </button>
        </div>
        {!isCheckoutStage &&
          !isFinalStage &&
          !isEatInStage1 &&
          !isEatInStage2 &&
          !isPickupFormStage &&
          cartItems.length > 0 && (
            <button className="clear-cart-button" onClick={clearCart}>
              Clear Cart
            </button>
          )}

        {isFinalStage ? (
          <div className="order-success">
            <i className="fab fa-whatsapp order-whatsapp-icon"></i>
            <h3>
              Your order will be sent via WhatsApp. Please ensure you're using a
              device that has WhatsApp installed.
            </h3>

            <button
              type="button"
              className="add-to-cart-button"
              onClick={placeOrder}
              disabled={isLoading}
            >
              {isLoading ? "Sending Order..." : "Send order to kitchen"}
            </button>
          </div>
        ) : isPickupFormStage ? (
          <>
            <div className="pickup-top">
              <i
                onClick={() => {
                  setIsPickupFormStage(false);
                  setTableNumber("");
                  setCustomerName("");
                  setNotes("");
                }}
                className="fas fa-arrow-left"
              ></i>
              <h3>Customer Info</h3>
            </div>

            <form className="eatin-form" onSubmit={confirmCheckout}>
              <div className="form-group eat-in-form-group checkout-form">
                <label htmlFor="customerName">Name</label>
                <input
                  type="text"
                  id="customerName"
                  value={customerName}
                  onChange={(e) => setCustomerName(e.target.value)}
                  required
                  placeholder="Enter your Name"
                />
              </div>

              <div className="form-group eat-in-form-group checkout-form">
                <label htmlFor="notes">Notes (optional)</label>
                <textarea
                  id="notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Add any special instructions..."
                  rows="3"
                  style={{ resize: "none" }}
                />
              </div>

              <button
                type="submit"
                className="add-to-cart-button"
                disabled={!customerName.trim()}
              >
                {isLoading ? "Sending Order..." : "Send order to kitchen"}
              </button>
            </form>
          </>
        ) : isEatInStage1 ? (
          <>
            <div className="eat-in-stage">
              <div className="pickup-top">
                <i
                  onClick={() => {
                    setIsCurrentlyInRestaurant(null);
                    setTableNumber("");
                    setCustomerName("");
                    setNotes("");
                    setIsEatInStage1(false);
                  }}
                  className="fas fa-arrow-left"
                ></i>
              </div>

              <div className="question-section">
                <p>Are you currently in the restaurant?</p>
                <div className="button-group">
                  <button
                    type="button"
                    className={`add-to-cart-button answer-button ${
                      isCurrentlyInRestaurant === true ? "selected" : ""
                    }`}
                    onClick={() => {
                      setIsCurrentlyInRestaurant(true);
                      setIsEatInStage1(false);
                      setIsEatInStage2(true);
                    }}
                  >
                    Yes
                  </button>
                  <button
                    type="button"
                    className={`add-to-cart-button answer-button ${
                      isCurrentlyInRestaurant === false ? "selected" : ""
                    }`}
                    onClick={() => {
                      setIsCurrentlyInRestaurant(false);
                      setIsEatInStage1(false);
                      setIsEatInStage2(true);
                    }}
                  >
                    No
                  </button>
                </div>
              </div>
            </div>
          </>
        ) : isEatInStage2 ? (
          <>
            <div className="eat-in-stage">
              <div className="pickup-top">
                <i
                  onClick={() => {
                    setIsEatInStage1(true);
                    setIsEatInStage2(false);
                    setTableNumber("");
                    setCustomerName("");
                    setNotes("");
                  }}
                  className="fas fa-arrow-left"
                ></i>
                <h3>Customer Info</h3>
              </div>

              {isCurrentlyInRestaurant ? (
                <form className="eatin-form" onSubmit={confirmEatinCheckout}>
                  <div className="form-group eat-in-form-group checkout-form">
                    <label htmlFor="tableNumber">Table Number</label>
                    <input
                      type="text"
                      id="tableNumber"
                      value={tableNumber}
                      onChange={(e) => setTableNumber(e.target.value)}
                      required
                      placeholder="Enter your table number"
                    />
                  </div>

                  <div className="form-group eat-in-form-group checkout-form">
                    <label htmlFor="customerName">Name</label>
                    <input
                      type="text"
                      id="customerName"
                      value={customerName}
                      onChange={(e) => setCustomerName(e.target.value)}
                      required
                      placeholder="Enter your name"
                    />
                  </div>

                  <div className="form-group eat-in-form-group checkout-form">
                    <label htmlFor="notes">Notes (optional)</label>
                    <textarea
                      id="notes"
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      placeholder="Add any special instructions..."
                      rows="3"
                      style={{ resize: "none" }}
                    />
                  </div>

                  <button
                    type="submit"
                    className="add-to-cart-button"
                    disabled={!customerName.trim() || !tableNumber.trim()}
                  >
                    {isLoading ? "Sending Order..." : "Send order to kitchen"}
                  </button>
                </form>
              ) : (
                <p
                  className="not-in-restaurant-message"
                  style={{
                    marginTop: "2rem",
                    fontWeight: "bold",
                    fontSize: "25px",
                    color: "#d9534f",
                  }}
                >
                  Please order when you're in the restaurant.
                </p>
              )}
            </div>
          </>
        ) : (
          !isCheckoutStage &&
          !isEatInStage1 &&
          !isEatInStage2 &&
          !isPickupFormStage && (
            <>
              {/* Cart View (Stage 1) */}
              {cartItems.length === 0 ? (
                <p className="empty-cart">Your cart is empty</p>
              ) : (
                <ul className="cart-items-list">
                  {cartItems.map((cartItem, index) => {
                    const { item, quantity } = cartItem;
                    const {
                      item_name,
                      image,
                      price,
                      selectedRequiredOption,
                      swallowOptions = [],
                      mainProteins = [],
                      extraProteins = [],
                    } = item;

                    return (
                      <li key={index} className="cart-item">
                        <div className="cart-item-main">
                          {image && (
                            <img
                              src={`https://lasgidirestaurant.com/${image}`}
                              alt={item_name}
                              className="cart-item-image"
                            />
                          )}
                          <div className="cart-item-details">
                            <div className="cart-item-details-top">
                              <h3>{item_name}</h3>
                              {price > 0 && (
                                <p className="menu-item-price2">
                                  £{parseFloat(price).toFixed(2)}
                                </p>
                              )}
                            </div>

                            {/* Required Option */}
                            {selectedRequiredOption && (
                              <div className="cart-option">
                                <span className="option-name">
                                  {selectedRequiredOption.option_name}
                                </span>
                                <span className="option-price">
                                  £
                                  {parseFloat(
                                    selectedRequiredOption.option_price
                                  ).toFixed(2)}
                                </span>
                              </div>
                            )}

                            {/* Swallow Options (no heading) */}
                            {swallowOptions.length > 0 &&
                              swallowOptions.map((option, idx) => (
                                <div key={idx} className="cart-option">
                                  <span className="option-name">
                                    {option.option_name}
                                    {option.quantity > 1
                                      ? ` (x${option.quantity})`
                                      : ""}
                                  </span>
                                  <span className="option-price">
                                    £
                                    {(
                                      parseFloat(option.option_price) *
                                      (option.quantity || 1)
                                    ).toFixed(2)}
                                  </span>
                                </div>
                              ))}

                            {/* Main Proteins (no heading) */}
                            {mainProteins.length > 0 &&
                              mainProteins.map((option, idx) => (
                                <div key={idx} className="cart-option">
                                  <span className="option-name">
                                    {option.option_name}
                                    {option.quantity > 1
                                      ? ` (x${option.quantity})`
                                      : ""}
                                  </span>
                                  <span className="option-price">
                                    £
                                    {(
                                      parseFloat(option.option_price) *
                                      (option.quantity || 1)
                                    ).toFixed(2)}
                                  </span>
                                </div>
                              ))}

                            {/* Extra Proteins */}
                            {extraProteins.length > 0 && (
                              <>
                                <p className="option-section-label">Extra:</p>
                                {extraProteins.map((option, idx) => (
                                  <div key={idx} className="cart-option">
                                    <span className="option-name">
                                      {option.option_name}
                                      {option.quantity > 1 &&
                                        ` (x${option.quantity})`}
                                    </span>
                                    <span className="option-price">
                                      £
                                      {(
                                        parseFloat(option.option_price) *
                                        (option.quantity || 1)
                                      ).toFixed(2)}
                                    </span>
                                  </div>
                                ))}
                              </>
                            )}

                            {/* Quantity + Total */}
                            <div className="cart-bottom">
                              <div className="quantity-controls">
                                <button
                                  className="cart-quantity-btn"
                                  onClick={() =>
                                    handleQuantityChange(cartItem.key, -1)
                                  }
                                >
                                  <i className="fas fa-minus"></i>
                                </button>
                                <span>{quantity}</span>
                                <button
                                  className="cart-quantity-btn"
                                  onClick={() =>
                                    handleQuantityChange(cartItem.key, 1)
                                  }
                                >
                                  <i className="fas fa-plus"></i>
                                </button>
                              </div>
                              <p className="cart-item-total">
                                £{getItemTotal(cartItem).toFixed(2)}
                              </p>
                            </div>
                          </div>
                        </div>

                        <div className="cart-item-actions">
                          <button
                            className="delete-btn"
                            onClick={() => removeItemFromCart(cartItem.key)}
                          >
                            <i className="fas fa-trash"></i>
                          </button>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
              <div className="checkout-section">
                <h3>Total: £{cartTotal.toFixed(2)}</h3>
                <div className="checkout-buttons">
                  <button
                    className="add-to-cart-button"
                    disabled={isCartEmpty}
                    onClick={() => handleCheckout("eat-in")}
                  >
                    Eat In
                  </button>
                  <button
                    className="add-to-cart-button"
                    disabled={isCartEmpty}
                    onClick={() => handleCheckout("pickup")}
                  >
                    Pick Up
                  </button>
                </div>
              </div>
            </>
          )
        )}
      </div>
    </div>
  );
}

export default CartModal;
